import pickBy from 'lodash/pickBy';
import qs from 'qs';

import { compactValue, difference, getDefaultFilters } from 'Components/VehicleList/VehicleList.helpers';
import type { Filter, FilterQuery, OptionalFilters } from 'Types/filter';
import type { MakeAndModel } from 'Utilities/makeModel/makeModel.types';
import { formatModelWildcardForServer, getQueryFromMakeModels } from 'Utilities/makeModel/makeModelQuery.helpers';

import type {
	ExcludeDefaultFiltersAndSortOrderParams,
	FormatFiltersToQueryParams,
	GetQueryParams,
	GetQueryPrefixParams,
	GetQueryStringParams,
} from './formatFiltersToQuery.types';

const getQueryString = ({ addQueryPrefix = false, queryObject }: GetQueryStringParams) =>
	qs.stringify(queryObject, { addQueryPrefix, indices: false });

export const getQueryPrefix = ({ makeModelQuery = '', otherQuery = '' }: GetQueryPrefixParams): string => {
	if (!makeModelQuery && otherQuery) {
		return '?';
	}
	if (makeModelQuery && otherQuery) {
		return '&';
	}
	return '';
};

export const getMakeModelQueryForClient = (makeAndModel: MakeAndModel): string =>
	getQueryFromMakeModels({ makeModels: Object.fromEntries(makeAndModel), queryPrefix: '?' });

export const getMakeModelQueryForServer = (makeAndModel: MakeAndModel): string => {
	const makeModel = { make: Object.fromEntries(makeAndModel) };
	const serverQuery = qs.stringify(makeModel, { addQueryPrefix: true, arrayFormat: 'repeat' });
	return formatModelWildcardForServer(serverQuery);
};

export const excludeDefaultFiltersAndSortOrder = ({
	defaultOrder,
	defaultSort,
	filters = {} as Filter,
}: ExcludeDefaultFiltersAndSortOrderParams): OptionalFilters => {
	const defaultSortOrder = { order: defaultOrder, sort: defaultSort };
	return difference(pickBy(filters, compactValue), {
		...getDefaultFilters(),
		...defaultSortOrder,
	});
};

export const getQuery = ({
	defaultOrder,
	defaultSort,
	filters,
	getMakeModelQuery,
	makeAndModel,
}: GetQueryParams): string => {
	const modifiedFilters = excludeDefaultFiltersAndSortOrder({ defaultOrder, defaultSort, filters });

	// using rest (otherFilters) to extract all filters apart
	// from make and model which are using different logic to create query string.
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { make, model, ...otherFilters } = modifiedFilters;

	const makeModelQuery = getMakeModelQuery(makeAndModel);
	const otherQuery = getQueryString({ queryObject: otherFilters });

	return `${makeModelQuery}${getQueryPrefix({ makeModelQuery, otherQuery })}${otherQuery}`;
};

export const getDisplayUrl = (query: string): string => `/vehicles${query}`;

export const getAsUrlPath = (query: string): string => `/vehicle_list${query}`;

export const formatFiltersToQuery = ({
	defaultOrder,
	defaultSort,
	filters,
	makeAndModel,
}: FormatFiltersToQueryParams): FilterQuery => {
	const query = getQuery({
		defaultOrder,
		defaultSort,
		filters,
		getMakeModelQuery: getMakeModelQueryForClient,
		makeAndModel,
	});
	const serverQuery = getQuery({
		defaultOrder,
		defaultSort,
		filters,
		getMakeModelQuery: getMakeModelQueryForServer,
		makeAndModel,
	});

	const displayUrl = getDisplayUrl(query);
	const asUrlPath = getAsUrlPath(query);

	return { asUrlPath, displayUrl, serverQuery };
};
